import type { Locale } from '@cibli/shared';

import {
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cibli/shared';
import { Globe, Lock, LogOut, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import ConfirmDialog from '@/components/ConfirmDialog';
import { useLanguageContext } from '@/contexts/language.context';
import { useUserContext } from '@/contexts/user.context';
import DashboardLayout from '@/features/common/components/DashboardLayout';
import { useResetToHome } from '@/features/common/hooks/useResetToHome';
import { getLocales } from '@/services/locale';
import { deleteAccount, updateUserLocale, updateUserPreferences } from '@/services/user';

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { logout, user, loginWithExistingUser, refreshUser } = useUserContext();
  const { setLanguage } = useLanguageContext();
  const { resetToHome } = useResetToHome();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [isSavingLocale, setIsSavingLocale] = useState(false);
  const [locales, setLocales] = useState<Locale[]>([]);

  useEffect(() => {
    getLocales().then(setLocales).catch(console.error);
  }, []);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      toast.success(t('dashboard.profile.deleteAccount.success'));
      await logout();
      resetToHome();
    } catch (error) {
      toast.error(t('dashboard.profile.deleteAccount.error'));
      console.error('Error deleting account:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handlePreferenceChange = async (value: string) => {
    setIsSavingPreferences(true);
    try {
      await updateUserPreferences(value === 'email');
      await loginWithExistingUser();
      toast.success(t('dashboard.profile.preferences.success'));
    } catch (error) {
      toast.error(t('dashboard.profile.preferences.error'));
      console.error('Error saving preferences:', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const handleLanguageChange = useCallback(
    async (codeLong: string) => {
      setIsSavingLocale(true);
      try {
        await updateUserLocale(codeLong);
        await setLanguage(codeLong);
        await refreshUser();
        toast.success(t('dashboard.profile.language.success'));
      } catch (error) {
        toast.error(t('dashboard.profile.language.error'));
        console.error('Error updating locale:', error);
      } finally {
        setIsSavingLocale(false);
      }
    },
    [setLanguage, refreshUser, t],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    resetToHome();
  }, [logout, resetToHome]);

  return (
    <>
      <DashboardLayout
        title={t('dashboard.profile.title')}
        subtitle={t('dashboard.profile.subtitle')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Left column: Profile + Info */}
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="bg-gradient-to-br from-cj-green-light to-cj-cream rounded-2xl p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cj-green/15 rounded-full blur-2xl" />
              <div className="relative z-10 flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-cj-green to-cj-green-nuance rounded-2xl flex items-center justify-center shadow-lg shadow-cj-green/30">
                  <span className="font-display text-xl text-white">
                    {user?.firstName?.[0]?.toUpperCase()}
                    {user?.lastName?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-lg text-cj-green-dark">
                    {user?.firstName} {user?.lastName}
                  </h3>
                  <p className="text-sm text-cj-green-dark/50 mt-1">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* User Info Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1">
                <p className="text-xs font-medium text-cj-green-dark/50">
                  {t('dashboard.profile.info.firstName')}
                </p>
                <p className="text-sm font-semibold text-cj-green-dark">{user?.firstName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-cj-green-dark/50">
                  {t('dashboard.profile.info.lastName')}
                </p>
                <p className="text-sm font-semibold text-cj-green-dark">{user?.lastName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-cj-green-dark/50">
                  {t('dashboard.profile.info.phone')}
                </p>
                <p className="text-sm font-semibold text-cj-green-dark">{user?.phoneNumber}</p>
              </div>
              {user?.email && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-cj-green-dark/50">
                    {t('dashboard.profile.info.email')}
                  </p>
                  <p className="text-sm font-semibold text-cj-green-dark">{user.email}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right column: Settings */}
          <div className="space-y-3">
            {/* Informations */}
            <div className="pro-card p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-cj-green-light/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-cj-green-nuance" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-cj-green-dark">
                    {t('dashboard.profile.informationSection')}
                  </p>
                  <p className="text-[10px] text-cj-green-dark/40">
                    {t('dashboard.profile.informationDescription')}
                  </p>
                </div>
              </div>
              <div className="pl-12">
                <p className="text-xs text-cj-green-dark/50 mb-2">
                  {t('dashboard.profile.preferences.description')}
                </p>
                <RadioGroup
                  value={user?.preferEmailNotification ? 'email' : 'sms'}
                  onValueChange={handlePreferenceChange}
                  disabled={isSavingPreferences}
                  className="flex gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="email"
                      id="pref-email"
                      disabled={!user?.email || isSavingPreferences}
                    />
                    <Label
                      htmlFor="pref-email"
                      className={!user?.email ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                    >
                      {t('dashboard.profile.preferences.email')}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="sms" id="pref-sms" disabled={isSavingPreferences} />
                    <Label htmlFor="pref-sms" className="cursor-pointer">
                      {t('dashboard.profile.preferences.sms')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Langue */}
            <div className="pro-card p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-cj-green-light/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-cj-green-nuance" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-cj-green-dark">
                    {t('dashboard.profile.language.title')}
                  </p>
                  <p className="text-[10px] text-cj-green-dark/40">
                    {locales.find(l => l.codeLong === i18n.language)?.name ??
                      t('dashboard.profile.language.fallback')}
                  </p>
                </div>
              </div>
              <div className="pl-12">
                <Select
                  value={locales.some(l => l.codeLong === i18n.language) ? i18n.language : ''}
                  onValueChange={codeLong => void handleLanguageChange(codeLong)}
                  disabled={locales.length === 0 || isSavingLocale}
                >
                  <SelectTrigger className="w-full max-w-xs" size="sm">
                    <SelectValue placeholder={t('dashboard.profile.language.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locales.map(locale => (
                      <SelectItem key={locale.codeLong} value={locale.codeLong}>
                        {locale.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Sécurité */}
            <div className="pro-card p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-cj-pink/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lock className="w-4 h-4 text-cj-pink" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-cj-green-dark">
                    {t('dashboard.profile.security.title')}
                  </p>
                  <p className="text-[10px] text-cj-green-dark/40">
                    {t('dashboard.profile.security.description')}
                  </p>
                </div>
              </div>

              <div className="pl-12">
                <p className="text-xs text-cj-green-dark/50 mb-2">
                  {t('dashboard.profile.deleteAccount.warning')}
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isDeleting}
                >
                  {t('dashboard.profile.deleteAccount.button')}
                </Button>
              </div>
            </div>

            {/* Se déconnecter */}
            <button
              onClick={handleLogout}
              className="w-full pro-card p-4 flex items-center gap-3 border-cj-pink/15 hover:bg-cj-pink/5 text-left"
            >
              <div className="w-9 h-9 bg-cj-pink/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <LogOut className="w-4 h-4 text-cj-pink" />
              </div>
              <p className="text-sm font-semibold text-cj-pink">{t('dashboard.profile.logout')}</p>
            </button>
          </div>
        </div>
      </DashboardLayout>

      <ConfirmDialog
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        title={t('dashboard.profile.deleteAccount.modal.title')}
        description={t('dashboard.profile.deleteAccount.modal.description')}
        onConfirm={handleDeleteAccount}
        confirmLabel={t('dashboard.profile.deleteAccount.modal.confirm')}
        cancelLabel={t('dashboard.profile.deleteAccount.modal.cancel')}
        confirmVariant="destructive"
        isLoading={isDeleting}
      />
    </>
  );
}
